import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PdsBox,
  PdsCard,
  PdsMantineText,
  PdsMantineButton,
  PdsTag,
  PdsLoader,
  PdsNotification,
  PdsSyncIcon,
  PdsSpinnerIcon,
  PdsChevronRightSmIcon,
  PdsChevronDownSmIcon,
} from '@podium-design-system/react-components';
import Pagination from './Pagination';
import { InvestigationDenseRow } from './InvestigationExpandable';
import InvestigatePairButtons from './InvestigatePairButtons';
import { urlScanRowMatchesKeywordFilters, isKeywordQueryActive } from '../utils/resultKeywordFilter';
import { formatPublicWwwRank, sortPublicWwwByRankAsc } from '../utils/publicWwwDisplay';
import { isPlausibleIpAddress } from '../utils/ipAddress';
import { isPlausibleInvestigateDomain } from '../utils/investigateDomain';
import { urlScanRowConnectionLabel, PUBLICWWW_EDGE_LABEL } from '../utils/investigationEdgeLabels';
import {
  urlScanInvestigationScanUrl,
  urlScanInvestigationSearchUrl,
  resolveUrlScanExternalUrl,
} from '../utils/urlScanExternalLink';

/** Stable default — `keywordFilters = []` in props would be a new array every render and reset pagination. */
const EMPTY_KEYWORD_FILTERS = [];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function PublicWwwRunPrompt({ onRun, running }) {
  return (
    <PdsBox direction="column" gap="sm" alignItems="flex-start">
      <PdsMantineText type="body" color="neutral" style={{ lineHeight: 1.55, margin: 0 }} component="p">
        Due to limited API usage for PublicWWW, tool does not run by default. If you want results from PublicWWW, click here
      </PdsMantineText>
      <PdsMantineButton
        rank="outline"
        color="action"
        size="sm"
        onClick={onRun}
        disabled={running || !onRun}
        leadingIcon={running ? <PdsSpinnerIcon /> : <PdsSyncIcon />}
      >
        {running ? 'Running…' : 'RUN'}
      </PdsMantineButton>
    </PdsBox>
  );
}

const SnippetHighlight = ({ snippet, term }) => {
  if (!snippet) return null;
  if (!term?.trim()) return <span style={{ wordBreak: 'break-word' }}>{snippet}</span>;
  const parts = snippet.split(new RegExp(`(${escapeRegExp(term.trim())})`, 'gi'));
  return (
    <span style={{ wordBreak: 'break-word' }}>
      {parts.map((part, i) =>
        part.toLowerCase() === term.trim().toLowerCase() ? <strong key={i}>{part}</strong> : part,
      )}
    </span>
  );
};

const truncateUrl = (url, maxLength = 80) => {
  if (!url || url.length <= maxLength) return url;
  return url.substring(0, maxLength) + '...';
};

/** User-visible labels for UrlScan row types (matches Investigation network edge vocabulary). */
export function formatUrlScanTypeFilterLabel(type) {
  if (!type) return '';
  switch (type) {
    case 'URL_SEARCH':
      return 'URLScan Search';
    case 'LINKS':
      return 'URLScan Scan · Http';
    case 'LINK_DOMAINS':
      return 'URLScan Scan · Links';
    case 'RELATED_DOMAIN':
      return 'URLScan Scan · Domains';
    case 'SCREENSHOT':
      return 'URLScan Scan · Screenshot';
    default:
      return String(type)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
  }
}

/** Short subtype label for card tags (no "URLScan Scan ·" / Search parent prefix). */
export function formatUrlScanCardTypeLabel(type) {
  if (!type) return '';
  switch (type) {
    case 'LINKS':
      return 'Http';
    case 'LINK_DOMAINS':
      return 'Links';
    case 'RELATED_DOMAIN':
      return 'Domains';
    case 'SCREENSHOT':
      return 'Screenshot';
    case 'URL_SEARCH':
      return 'Search';
    default:
      return String(type)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
  }
}

const URL_BODY_WRAP = { wordBreak: 'break-word', overflowWrap: 'anywhere' };

function getHostnameForGrouping(value) {
  if (!value || typeof value !== 'string') return null;
  const t = value.trim();
  if (!t.startsWith('http://') && !t.startsWith('https://')) return null;
  try {
    return new URL(t).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function urlScanRowTimeMs(row) {
  return new Date(row?.sourceRetrievedAt || row?.createdAt || 0).getTime();
}

function sortUrlScanRowsNewestFirst(rows) {
  return [...rows].sort((a, b) => urlScanRowTimeMs(b) - urlScanRowTimeMs(a));
}

/** Groups http(s) rows that share a hostname into one entry; singles stay as single rows. */
function buildHostnameGroups(rows) {
  const sorted = sortUrlScanRowsNewestFirst(rows);
  const byHost = new Map();
  const noHost = [];
  for (const row of sorted) {
    const host = getHostnameForGrouping(row.informationValue);
    if (!host) {
      noHost.push(row);
      continue;
    }
    if (!byHost.has(host)) byHost.set(host, []);
    byHost.get(host).push(row);
  }
  const out = [];
  for (const [host, items] of byHost) {
    const groupItems = sortUrlScanRowsNewestFirst(items);
    if (groupItems.length > 1) {
      out.push({ kind: 'group', host, items: groupItems });
    } else {
      out.push({ kind: 'single', item: groupItems[0] });
    }
  }
  for (const row of noHost) {
    out.push({ kind: 'single', item: row });
  }
  out.sort((a, b) => {
    const ta = a.kind === 'group'
      ? Math.max(...a.items.map(urlScanRowTimeMs))
      : urlScanRowTimeMs(a.item);
    const tb = b.kind === 'group'
      ? Math.max(...b.items.map(urlScanRowTimeMs))
      : urlScanRowTimeMs(b.item);
    return tb - ta;
  });
  return out;
}

async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* ignore */
  }
}

function UrlScanOpenLink({ href, size = 'xs', label = 'Open on URLScan' }) {
  if (!href) return null;
  return (
    <PdsMantineButton
      rank="outline"
      color="neutral"
      size={size}
      onClick={(e) => {
        e.stopPropagation();
        window.open(href, '_blank', 'noopener,noreferrer');
      }}
    >
      {label}
    </PdsMantineButton>
  );
}

const UrlScanInformation = ({
  investigationId,
  /** Investigation node target — used for section-level UrlScan.io links. */
  investigationTarget = null,
  preloadedRows,
  onInvestigate,
  onAddNodeOnly,
  keywordFilters = EMPTY_KEYWORD_FILTERS,
  onlyTypes = null,
  /** @type {'urlscan' | 'publicwww'} — PublicWWW rows are loaded from `/publicwww-information`, not UrlScan. */
  variant = 'urlscan',
  highlightTerm = null,
  viewDensity = 'comfortable',
  /** When rows are empty, show this instead of the default italic line (e.g. persisted tool outcome). */
  emptyHint = null,
  /** Case investigation page: split UrlScan direct scan vs search; show “not run” when flags are false. */
  caseInvestigationView = false,
  /** When provided (case view), hide map-only "+" if this row's pivot already exists as a node's target on the chart. */
  caseMapHasTarget,
  workflowRequestedTools = null,
  investigationTerminal = false,
  /** Case PublicWWW tab: run PublicWWW on demand when it was not included in the workflow. */
  onRunPublicWww,
  publicWwwRunning = false,
  /** Increment to refetch rows after a PublicWWW run completes. */
  dataRefreshNonce = 0,
}) => {
  const [urlScanData, setUrlScanData] = useState(() => (preloadedRows ? preloadedRows : []));
  const [loading, setLoading] = useState(!preloadedRows);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState('ALL');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  /** Case view: collapsible URLScan Scan / URLScan Search sections */
  const [directScanExpanded, setDirectScanExpanded] = useState(false);
  const [searchHistoricalExpanded, setSearchHistoricalExpanded] = useState(false);
  /** Case UrlScan Scan section: merge rows that share the same http(s) hostname */
  const [groupByHostEnabled, setGroupByHostEnabled] = useState(true);
  const [searchListPage, setSearchListPage] = useState(1);
  const [searchItemsPerPage, setSearchItemsPerPage] = useState(20);

  useEffect(() => {
    setSelectedFilter('ALL');
    setCurrentPage(1);
    setSearchListPage(1);
    setDirectScanExpanded(false);
    setSearchHistoricalExpanded(false);
  }, [investigationId]);

  useEffect(() => {
    setSearchListPage(1);
  }, [keywordFilters]);

  useEffect(() => {
    if (preloadedRows) {
      setUrlScanData(preloadedRows);
      setLoading(false);
      setError(null);
      return;
    }
    if (!investigationId) {
      setLoading(false);
      return;
    }
    const path =
      variant === 'publicwww' ? 'publicwww-information' : 'urlscan-information';
    const fetch = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/investigations/${investigationId}/${path}`);
        setUrlScanData(response.data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId, preloadedRows, variant, dataRefreshNonce]);

  useEffect(() => {
    setCurrentPage(1);
  }, [keywordFilters]);

  const handleViewScreenshot = (screenshotUrl) => {
    const popup = window.open('', 'screenshot', 'width=1200,height=800,scrollbars=yes,resizable=yes');
    if (popup) {
      popup.document.write(`<!DOCTYPE html><html><head><title>URLScan Screenshot</title>
        <style>body{margin:0;padding:20px;background:#f6f8fa;font-family:-apple-system,sans-serif}
        img{max-width:100%;height:auto;border:1px solid #d0d7de;border-radius:6px}
        .url{font-size:14px;color:#656d76;word-break:break-all;margin-bottom:16px}</style></head>
        <body><div class="url">${screenshotUrl}</div>
        <img src="${screenshotUrl}" alt="Screenshot" onerror="this.style.display='none'"/>
        </body></html>`);
      popup.document.close();
    }
  };

  const canInvestigateEntry = (info) => {
    const v = info.informationValue;
    if (v == null) return false;
    const s = String(v).trim();
    if (isPlausibleIpAddress(s)) return true;
    if (['DOMAIN', 'URL', 'SUBDOMAIN', 'RELATED_DOMAIN', 'LINK_DOMAINS', 'PUBLICWWW_WEBSITE', 'LINKS', 'SCREENSHOT'].includes(info.informationType)) {
      return true;
    }
    if (s.startsWith('http://') || s.startsWith('https://')) {
      try {
        const h = new URL(s).hostname;
        if (isPlausibleIpAddress(h) || isPlausibleInvestigateDomain(h)) return true;
      } catch {
        /* fall through */
      }
    }
    return /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}/.test(s);
  };

  const extractDomain = (info) => {
    const value = info.informationValue;
    if (value.startsWith('http://') || value.startsWith('https://')) {
      try { return new URL(value).hostname; } catch {
        const m = value.match(/^https?:\/\/([^/]+)/);
        return m ? m[1] : value;
      }
    }
    return value;
  };

  /** Hostname, IPv4, or IPv6 literal for pivoting an investigation (not a URL path). */
  const extractInvestigateTarget = (info) => {
    const value = info.informationValue;
    if (value == null) return '';
    const t = String(value).trim();
    if (isPlausibleIpAddress(t)) return t;
    if (t.startsWith('http://') || t.startsWith('https://')) {
      try {
        const h = new URL(t).hostname;
        return h || extractDomain(info);
      } catch {
        const m = t.match(/^https?:\/\/([^/?#]+)/);
        return m ? m[1].replace(/^\[|\]$/g, '') : extractDomain(info);
      }
    }
    return extractDomain(info);
  };

  const investigateEdgeLabelForRow = (row) =>
    variant === 'publicwww' ? PUBLICWWW_EDGE_LABEL : urlScanRowConnectionLabel(row?.informationType);

  if (loading) {
    return (
      <PdsBox justifyContent="center" padding="md">
        <PdsLoader size="sm" />
      </PdsBox>
    );
  }

  if (error) {
    const label = variant === 'publicwww' ? 'PublicWWW' : 'URLScan';
    return <PdsNotification color="warning" dismissible={false}>Error loading {label} data: {error}</PdsNotification>;
  }

  const publicWwwTableMode =
    variant === 'publicwww' ||
    (onlyTypes?.length === 1 && onlyTypes[0] === 'PUBLICWWW_WEBSITE');

  let baseData = onlyTypes?.length
    ? urlScanData.filter((item) => onlyTypes.includes(item.informationType))
    : urlScanData;

  if (baseData.length === 0) {
    if (
      caseInvestigationView &&
      variant === 'publicwww' &&
      workflowRequestedTools?.enablePublicWww !== true &&
      (investigationTerminal || publicWwwRunning)
    ) {
      return <PublicWwwRunPrompt onRun={onRunPublicWww} running={publicWwwRunning} />;
    }
    if (
      caseInvestigationView &&
      investigationTerminal &&
      variant === 'urlscan' &&
      workflowRequestedTools?.enableUrlScan === false &&
      workflowRequestedTools?.enableUrlScanSearch === false
    ) {
      return (
        <PdsMantineText type="body" color="neutral" style={{ margin: 0, lineHeight: 1.55 }} component="p">
          URLScan (direct scan and search) was not run for this node. Use <strong>Re-run tools</strong> to
          include them.
        </PdsMantineText>
      );
    }
    const fallback = publicWwwTableMode
      ? 'No PublicWWW results for this node for the current query.'
      : 'No URLScan information for this node for the current query.';
    return (
      <PdsMantineText type="body" color="neutral" style={{ fontStyle: 'italic', margin: 0 }} component="p">
        {emptyHint || fallback}
      </PdsMantineText>
    );
  }

  const uniqueTypes = [...new Set(baseData.map(item => item.informationType))].sort();
  const filteredData = selectedFilter === 'ALL'
    ? baseData
    : baseData.filter(item => item.informationType === selectedFilter);

  const keywordFilteredData = !isKeywordQueryActive(keywordFilters)
    ? filteredData
    : filteredData.filter((item) => urlScanRowMatchesKeywordFilters(item, keywordFilters));

  const listForPagination = publicWwwTableMode
    ? sortPublicWwwByRankAsc(keywordFilteredData)
    : sortUrlScanRowsNewestFirst(keywordFilteredData);

  const scanBlockData = sortUrlScanRowsNewestFirst(
    keywordFilteredData.filter((i) => i.informationType !== 'URL_SEARCH'),
  );
  const searchBlockData = sortUrlScanRowsNewestFirst(
    keywordFilteredData.filter((i) => i.informationType === 'URL_SEARCH'),
  );
  const urlScanScanExternalUrl = urlScanInvestigationScanUrl(investigationTarget, scanBlockData);
  const urlScanSearchExternalUrl = urlScanInvestigationSearchUrl(investigationTarget);

  const handleFilterChange = (newFilter) => {
    setSelectedFilter(newFilter);
    setCurrentPage(1);
    setSearchListPage(1);
  };

  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  const handleSearchPageSizeChange = (newSize) => {
    setSearchItemsPerPage(newSize);
    setSearchListPage(1);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = listForPagination.slice(startIndex, startIndex + itemsPerPage);

  const compact = viewDensity === 'compact';

  const formatSourceTime = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return String(iso);
    }
  };

  if (caseInvestigationView && variant === 'urlscan') {
    const showScanSkipped =
      investigationTerminal && workflowRequestedTools?.enableUrlScan === false && scanBlockData.length === 0;
    const showSearchSkipped =
      investigationTerminal &&
      workflowRequestedTools?.enableUrlScanSearch === false &&
      searchBlockData.length === 0;

    const scanEntries = groupByHostEnabled
      ? buildHostnameGroups(scanBlockData)
      : scanBlockData.map((item) => ({ kind: 'single', item }));

    const scanCountLabel = groupByHostEnabled
      ? `${scanEntries.length} · ${scanBlockData.length} URLs`
      : String(scanBlockData.length);
    const searchCountLabel = `${searchBlockData.length} result${searchBlockData.length !== 1 ? 's' : ''}`;

    const renderInfoMetaLines = (info) => (
      <>
        {info.informationType === 'URL_SEARCH' && info.sourceRetrievedAt && (
          <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
            Result time (URLScan): {formatSourceTime(info.sourceRetrievedAt)}
          </PdsMantineText>
        )}
        {info.informationType === 'URL_SEARCH' && !info.sourceRetrievedAt && info.createdAt && (
          <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
            Stored at: {new Date(info.createdAt).toLocaleString()} (scan time not provided by API)
          </PdsMantineText>
        )}
        {info.informationType !== 'URL_SEARCH' && info.sourceRetrievedAt && (
          <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
            Result time (URLScan): {formatSourceTime(info.sourceRetrievedAt)}
          </PdsMantineText>
        )}
        {info.informationType !== 'URL_SEARCH' && !info.sourceRetrievedAt && info.createdAt && (
          <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
            Added: {new Date(info.createdAt).toLocaleString()}
          </PdsMantineText>
        )}
      </>
    );

    const mapRow = (info, idx, startIndex) => {
      const typeLabel = formatUrlScanCardTypeLabel(info.informationType);
      const headerActions = (
        <PdsBox direction="row" gap="xs" alignItems="center" flexWrap="wrap">
          {info.informationType === 'URL_SEARCH' && (
            <UrlScanOpenLink href={resolveUrlScanExternalUrl(info)} label="URLScan" />
          )}
          {info.informationType === 'SCREENSHOT' && info.screenshotUrl && (
            <PdsMantineButton
              rank="outline"
              color="neutral"
              size="xs"
              onClick={() => handleViewScreenshot(info.screenshotUrl)}
            >
              View
            </PdsMantineButton>
          )}
          {canInvestigateEntry(info) && onInvestigate && (
            <InvestigatePairButtons
              targetDomain={extractInvestigateTarget(info)}
              deriveFrom={info.informationType}
              connectionLabel={investigateEdgeLabelForRow(info)}
              onRunTools={onInvestigate}
              onAddNodeOnly={onAddNodeOnly}
              showAddNode={!caseMapHasTarget?.(extractInvestigateTarget(info))}
            />
          )}
        </PdsBox>
      );
      const inner = (
        <PdsBox direction="column" gap="sm" className="argus-stack-tight">
          <PdsBox direction="row" justifyContent="space-between" alignItems="center" gap="xs" wrap="wrap">
            <PdsTag color="action" dismissible={false} size="xs">
              {typeLabel}
            </PdsTag>
            {!compact && headerActions}
          </PdsBox>
          <PdsMantineText
            type="textline"
            component="span"
            className="argus-data"
            style={URL_BODY_WRAP}
            title={info.informationValue}
          >
            {compact ? truncateUrl(info.informationValue, 120) : info.informationValue}
          </PdsMantineText>
          {renderInfoMetaLines(info)}
        </PdsBox>
      );
      if (compact) {
        return (
          <InvestigationDenseRow
            key={info.id != null ? info.id : `${info.informationType}-${info.informationValue}-${startIndex + idx}`}
            dense
            variant="column"
            titlePrefix={`${typeLabel}:`}
            titleValue={truncateUrl(info.informationValue, 56)}
            actions={headerActions}
          >
            {inner}
          </InvestigationDenseRow>
        );
      }
      return (
        <PdsCard
          key={info.id != null ? info.id : `${info.informationType}-${info.informationValue}-${startIndex + idx}`}
          border
          borderRadius="sm"
          padding="sm"
          direction="column"
          gap="sm"
          color="neutral"
          contrast="low"
        >
          {inner}
        </PdsCard>
      );
    };

    const renderCaseEntry = (entry, idx, keyPrefix) => {
      if (entry.kind === 'group') {
        return (
          <PdsCard
            key={`${keyPrefix}-grp-${entry.host}`}
            border
            borderRadius="sm"
            padding="sm"
            direction="column"
            gap="sm"
            color="neutral"
            contrast="low"
          >
            <PdsBox direction="row" justifyContent="space-between" alignItems="flex-start" gap="sm" wrap="wrap">
              <PdsMantineText type="textline" fontWeight="strong" component="span" style={URL_BODY_WRAP}>
                {entry.host}
              </PdsMantineText>
              <PdsMantineButton type="button" size="xs" rank="ghost" color="neutral" onClick={() => copyTextToClipboard(entry.host)}>
                Copy host
              </PdsMantineButton>
            </PdsBox>
            <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
              {entry.items.length} URLs · same hostname
            </PdsMantineText>
            <PdsBox direction="column" gap="sm" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {entry.items.map((info) => (
                <PdsBox
                  key={info.id}
                  direction="column"
                  gap="xs"
                  style={{ borderTop: '1px solid #eaeef2', paddingTop: 8 }}
                >
                  <PdsBox direction="row" justifyContent="flex-end" alignItems="center" gap="xs" flexWrap="wrap">
                    {canInvestigateEntry(info) && onInvestigate && (
                      <InvestigatePairButtons
                        targetDomain={extractInvestigateTarget(info)}
                        deriveFrom={info.informationType}
                        connectionLabel={investigateEdgeLabelForRow(info)}
                        onRunTools={onInvestigate}
                        onAddNodeOnly={onAddNodeOnly}
                        showAddNode={!caseMapHasTarget?.(extractInvestigateTarget(info))}
                      />
                    )}
                  </PdsBox>
                  <a
                    href={info.informationValue}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="argus-data"
                    style={{ ...URL_BODY_WRAP, fontSize: 13 }}
                  >
                    {info.informationValue}
                  </a>
                  <PdsTag color="action" dismissible={false} size="xs" style={{ alignSelf: 'flex-start' }}>
                    {formatUrlScanCardTypeLabel(info.informationType)}
                  </PdsTag>
                  {renderInfoMetaLines(info)}
                </PdsBox>
              ))}
            </PdsBox>
          </PdsCard>
        );
      }
      return mapRow(entry.item, idx, 0);
    };

    const renderUrlScanSearchCardList = (rows) => {
      const searchStart = (searchListPage - 1) * searchItemsPerPage;
      const pageRows = rows.slice(searchStart, searchStart + searchItemsPerPage);

      return (
        <PdsBox direction="column" gap="md">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
            {pageRows.map((info, idx) => mapRow(info, idx, searchStart))}
          </div>
          <Pagination
            currentPage={searchListPage}
            totalItems={rows.length}
            itemsPerPage={searchItemsPerPage}
            onPageChange={setSearchListPage}
            onPageSizeChange={handleSearchPageSizeChange}
          />
        </PdsBox>
      );
    };

    const typeFilterBar = (
      <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center" className="argus-filter-bar">
        <span className="argus-type-label">URLScan types:</span>
        <PdsMantineButton
          rank={selectedFilter === 'ALL' ? 'fill' : 'outline'}
          color={selectedFilter === 'ALL' ? 'action' : 'neutral'}
          size="xs"
          onClick={() => handleFilterChange('ALL')}
        >
          All ({baseData.length})
        </PdsMantineButton>
        {uniqueTypes.map((type) => {
          const count = baseData.filter((item) => item.informationType === type).length;
          const label = formatUrlScanTypeFilterLabel(type);
          return (
            <PdsMantineButton
              key={type}
              rank={selectedFilter === type ? 'fill' : 'outline'}
              color={selectedFilter === type ? 'action' : 'neutral'}
              size="xs"
              onClick={() => handleFilterChange(type)}
            >
              {label} ({count})
            </PdsMantineButton>
          );
        })}
      </PdsBox>
    );

    const sectionToolbar = (
      <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center">
        <PdsMantineButton
          type="button"
          size="xs"
          rank="outline"
          color="neutral"
          onClick={() => {
            setDirectScanExpanded(true);
            setSearchHistoricalExpanded(true);
          }}
        >
          Expand all sections
        </PdsMantineButton>
        <PdsMantineButton
          type="button"
          size="xs"
          rank="outline"
          color="neutral"
          onClick={() => {
            setDirectScanExpanded(false);
            setSearchHistoricalExpanded(false);
          }}
        >
          Collapse all sections
        </PdsMantineButton>
        <PdsMantineButton
          type="button"
          size="xs"
          rank={groupByHostEnabled ? 'fill' : 'outline'}
          color={groupByHostEnabled ? 'action' : 'neutral'}
          onClick={() => setGroupByHostEnabled((v) => !v)}
        >
          Group scan by hostname
        </PdsMantineButton>
      </PdsBox>
    );

    const sectionShell = (expanded, onToggle, title, countLabel, body, externalHref) => (
      <PdsBox
        direction="column"
        gap={0}
        style={{
          border: '1px solid #e1e4e8',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: '10px 12px',
            background: '#f6f8fa',
            borderBottom: expanded ? '1px solid #e1e4e8' : 'none',
          }}
        >
          <button
            type="button"
            onClick={() => onToggle((v) => !v)}
            aria-expanded={expanded}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              padding: 0,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {expanded ? <PdsChevronDownSmIcon /> : <PdsChevronRightSmIcon />}
            <PdsMantineText type="headline" fontWeight="strong" component="span">
              {title}
            </PdsMantineText>
            <PdsTag color="neutral" dismissible={false} size="xs">
              {countLabel}
            </PdsTag>
          </button>
          {externalHref ? <UrlScanOpenLink href={externalHref} /> : null}
        </div>
        {expanded ? (
          <PdsBox direction="column" gap="sm" style={{ padding: 12 }}>
            {body}
          </PdsBox>
        ) : null}
      </PdsBox>
    );

    return (
      <PdsBox direction="column" gap="md">
        {typeFilterBar}
        {sectionToolbar}
        <PdsBox direction="column" gap="lg">
          {sectionShell(
            directScanExpanded,
            setDirectScanExpanded,
            'URLScan Scan',
            scanCountLabel,
            showScanSkipped ? (
              <PdsMantineText type="body" color="neutral" style={{ lineHeight: 1.55 }} component="p">
                Direct URLScan was not run for this node. Use <strong>Re-run tools</strong> to include it.
              </PdsMantineText>
            ) : scanBlockData.length === 0 ? (
              <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">
                No direct scan rows (screenshots, related domains, links) for this filter or query yet.
              </PdsMantineText>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
                {scanEntries.map((entry, idx) => renderCaseEntry(entry, idx, 'scan'))}
              </div>
            ),
            urlScanScanExternalUrl,
          )}
          {sectionShell(
            searchHistoricalExpanded,
            setSearchHistoricalExpanded,
            'URLScan Search',
            searchCountLabel,
            showSearchSkipped ? (
              <PdsMantineText type="body" color="neutral" style={{ lineHeight: 1.55 }} component="p">
                URLScan Search was not run for this node. Use <strong>Re-run tools</strong> to include it.
              </PdsMantineText>
            ) : searchBlockData.length === 0 ? (
              <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span">
                No search rows for this filter or query yet.
              </PdsMantineText>
            ) : (
              renderUrlScanSearchCardList(searchBlockData)
            ),
            urlScanSearchExternalUrl,
          )}
        </PdsBox>
      </PdsBox>
    );
  }

  if (publicWwwTableMode) {
    if (compact) {
      return (
        <PdsBox direction="column" gap="md">
          <PdsBox direction="column" gap="xs">
            {listForPagination.length === 0 ? (
              <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span" className="argus-type-meta">
                {filteredData.length === 0 ? 'No entries.' : 'No entries match the current keyword filters.'}
              </PdsMantineText>
            ) : (
              paginatedData.map((info) => (
                <InvestigationDenseRow
                  key={info.id}
                  dense
                  variant="column"
                  titlePrefix={`#${info.publicWwwRank != null ? info.publicWwwRank : '—'} ·`}
                  titleValue={truncateUrl(info.informationValue, 72)}
                  actions={
                    canInvestigateEntry(info) && onInvestigate ? (
                      <InvestigatePairButtons
                        targetDomain={extractInvestigateTarget(info)}
                        deriveFrom={info.informationType}
                        connectionLabel={investigateEdgeLabelForRow(info)}
                        onRunTools={onInvestigate}
                        onAddNodeOnly={onAddNodeOnly}
                        showAddNode={!caseMapHasTarget?.(extractInvestigateTarget(info))}
                      />
                    ) : null
                  }
                >
                  <PdsBox direction="column" gap="xs" className="argus-stack-tight">
                    <a
                      href={info.informationValue}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="argus-data"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {info.informationValue}
                    </a>
                    <PdsMantineText type="textline" component="span" className="argus-type-row-meta">
                      <SnippetHighlight snippet={info.publicWwwSnippet} term={highlightTerm} />
                    </PdsMantineText>
                  </PdsBox>
                </InvestigationDenseRow>
              ))
            )}
          </PdsBox>
          <Pagination
            currentPage={currentPage}
            totalItems={listForPagination.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </PdsBox>
      );
    }
    return (
      <PdsBox direction="column" gap="md">
        <div style={{ overflowX: 'auto', border: '1px solid #d0d7de', borderRadius: 8 }}>
          <table className="argus-table-investigation">
            <thead>
              <tr style={{ background: '#f6f8fa', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', whiteSpace: 'nowrap' }}>Rank</th>
                <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de' }}>Url</th>
                <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de' }}>Snippets</th>
                <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', width: 100 }}> </th>
              </tr>
            </thead>
            <tbody>
              {listForPagination.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: 12 }}>
                    {filteredData.length === 0 ? 'No entries.' : 'No entries match the current keyword filters.'}
                  </td>
                </tr>
              ) : (
                paginatedData.map((info) => (
                  <tr key={info.id} style={{ verticalAlign: 'top' }}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2', fontVariantNumeric: 'tabular-nums' }}>
                      {formatPublicWwwRank(info.publicWwwRank)}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }}>
                      <a href={info.informationValue} target="_blank" rel="noopener noreferrer" className="argus-data">
                        {info.informationValue}
                      </a>
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }} className="argus-type-row-meta">
                      <SnippetHighlight snippet={info.publicWwwSnippet} term={highlightTerm} />
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }}>
                      {canInvestigateEntry(info) && onInvestigate && (
                        <InvestigatePairButtons
                          targetDomain={extractInvestigateTarget(info)}
                          deriveFrom={info.informationType}
                          connectionLabel={investigateEdgeLabelForRow(info)}
                          onRunTools={onInvestigate}
                          onAddNodeOnly={onAddNodeOnly}
                          showAddNode={!caseMapHasTarget?.(extractInvestigateTarget(info))}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={listForPagination.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </PdsBox>
    );
  }

  return (
    <PdsBox direction="column" gap="md">
      {/* Filter bar */}
      {!publicWwwTableMode && (
      <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center" className="argus-filter-bar">
        <span className="argus-type-label">URLScan types:</span>
        <PdsMantineButton
          rank={selectedFilter === 'ALL' ? 'fill' : 'outline'}
          color={selectedFilter === 'ALL' ? 'action' : 'neutral'}
          size="xs"
          onClick={() => handleFilterChange('ALL')}
        >
          All ({baseData.length})
        </PdsMantineButton>
        {uniqueTypes.map(type => {
          const count = baseData.filter(item => item.informationType === type).length;
          const label = formatUrlScanTypeFilterLabel(type);
          return (
            <PdsMantineButton
              key={type}
              rank={selectedFilter === type ? 'fill' : 'outline'}
              color={selectedFilter === type ? 'action' : 'neutral'}
              size="xs"
              onClick={() => handleFilterChange(type)}
            >
              {label} ({count})
            </PdsMantineButton>
          );
        })}
      </PdsBox>
      )}

      {/* Section-level UrlScan.io links (one per scan / search of investigation target) */}
      {variant === 'urlscan' && investigationTarget && (urlScanScanExternalUrl || urlScanSearchExternalUrl) && (
        <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center">
          {urlScanScanExternalUrl && scanBlockData.length > 0 && (
            <UrlScanOpenLink href={urlScanScanExternalUrl} label="Open scan on URLScan" />
          )}
          {urlScanSearchExternalUrl && searchBlockData.length > 0 && (
            <UrlScanOpenLink href={urlScanSearchExternalUrl} label="Open search on URLScan" />
          )}
        </PdsBox>
      )}

      {/* Records grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        {keywordFilteredData.length === 0 ? (
          <PdsMantineText type="textline" color="neutral" style={{ fontStyle: 'italic' }} component="span" className="argus-type-meta">
            {filteredData.length === 0
              ? (selectedFilter === 'ALL'
                ? 'No entries found.'
                : `No ${formatUrlScanTypeFilterLabel(selectedFilter)} entries found.`)
              : 'No entries match the current keyword filters.'}
          </PdsMantineText>
        ) : (
          paginatedData.map((info, idx) => {
            const typeLabel = formatUrlScanCardTypeLabel(info.informationType);
            const headerActions = (
              <PdsBox direction="row" gap="xs" alignItems="center" flexWrap="wrap">
                {info.informationType === 'URL_SEARCH' && (
                  <UrlScanOpenLink href={resolveUrlScanExternalUrl(info)} label="URLScan" />
                )}
                {info.informationType === 'SCREENSHOT' && info.screenshotUrl && (
                  <PdsMantineButton
                    rank="outline"
                    color="neutral"
                    size="xs"
                    onClick={() => handleViewScreenshot(info.screenshotUrl)}
                  >
                    View
                  </PdsMantineButton>
                )}
                {canInvestigateEntry(info) && onInvestigate && (
                  <InvestigatePairButtons
                    targetDomain={extractInvestigateTarget(info)}
                    deriveFrom={info.informationType}
                    connectionLabel={investigateEdgeLabelForRow(info)}
                    onRunTools={onInvestigate}
                    onAddNodeOnly={onAddNodeOnly}
                    showAddNode={!caseMapHasTarget?.(extractInvestigateTarget(info))}
                  />
                )}
              </PdsBox>
            );
            const inner = (
              <PdsBox direction="column" gap="sm" className="argus-stack-tight">
                <PdsBox direction="row" justifyContent="space-between" alignItems="center" gap="xs" wrap="wrap">
                  <PdsTag color="action" dismissible={false} size="xs">
                    {typeLabel}
                  </PdsTag>
                  {!compact && headerActions}
                </PdsBox>
                <PdsMantineText
                  type="textline"
                  component="span"
                  className="argus-data"
                  style={URL_BODY_WRAP}
                  title={info.informationValue}
                >
                  {compact ? truncateUrl(info.informationValue, 120) : info.informationValue}
                </PdsMantineText>
                {info.informationType === 'URL_SEARCH' && info.sourceRetrievedAt && (
                  <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                    Result time (URLScan): {formatSourceTime(info.sourceRetrievedAt)}
                  </PdsMantineText>
                )}
                {info.informationType === 'URL_SEARCH' && !info.sourceRetrievedAt && info.createdAt && (
                  <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                    Stored at: {new Date(info.createdAt).toLocaleString()} (scan time not provided by API)
                  </PdsMantineText>
                )}
                {info.informationType !== 'URL_SEARCH' && info.createdAt && (
                <PdsMantineText type="textline" color="neutral" component="span" className="argus-type-meta">
                  Added: {new Date(info.createdAt).toLocaleString()}
                </PdsMantineText>
                )}
              </PdsBox>
            );
            if (compact) {
              return (
                <InvestigationDenseRow
                  key={info.id != null ? info.id : `${info.informationType}-${info.informationValue}-${startIndex + idx}`}
                  dense
                  variant="column"
                  titlePrefix={`${typeLabel}:`}
                  titleValue={truncateUrl(info.informationValue, 56)}
                  actions={headerActions}
                >
                  {inner}
                </InvestigationDenseRow>
              );
            }
            return (
              <PdsCard
                key={info.id != null ? info.id : `${info.informationType}-${info.informationValue}-${startIndex + idx}`}
                border
                borderRadius="sm"
                padding="sm"
                direction="column"
                gap="sm"
                color="neutral"
                contrast="low"
              >
                {inner}
              </PdsCard>
            );
          })
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={keywordFilteredData.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </PdsBox>
  );
};

export default UrlScanInformation;
